import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { useCallback, useState } from 'react';
import { projectAtom } from '@data/auth';
import type { AxiosResponse, AxiosError } from 'axios';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import { useTranslation } from 'react-i18next';
import { environmentAtom } from '@data/fms/environment/states';
import type { AreaMapServiceQuota } from '@data/fms/areamap/types';
import { useAtomCallback } from 'jotai/utils';

/**
 * エリアマップサービスクォータ取得API
 */
export const useAreaMapServiceQuotaAPI = (): {
  state: APIState;
  getAreaMapServiceQuota: () => Promise<AreaMapServiceQuota | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getAreaMapServiceQuotaAPI = useCallback(
    async (
      projectId: string,
      areaMapId: number,
    ): Promise<Result<AreaMapServiceQuota, AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/area_maps/${areaMapId}/service_quotas`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getAreaMapServiceQuota = useAtomCallback(
    useCallback(
      async (get) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.get_area_map_service_quota', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await getAreaMapServiceQuotaAPI(
          project.id,
          environment.area_map_id,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_area_map_service_quota', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        setState('hasValue');
        return res.value;
      },
      [notifyError, getAreaMapServiceQuotaAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    getAreaMapServiceQuota,
  };
};
